import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Toast, { useToast } from "../components/Toast";
import { obtenerOrden, guardarFirma } from "../services/ordenesServicioService";

export default function OrdenesServicioFirmaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toasts, closeToast, success: ok, error: err } = useToast();

  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [orden, setOrden] = useState(null);
  const [saving, setSaving] = useState(false);
  const [firmada, setFirmada] = useState(false);
  const [hasStroke, setHasStroke] = useState(false);
  const lastPos = useRef(null);

  useEffect(() => {
    obtenerOrden(id)
      .then(o => {
        setOrden(o);
        if (o.firmaImagen) setFirmada(true);
      })
      .catch(() => err("No se pudo cargar la orden"));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resize canvas to fill its container
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width  = rect.width;
      canvas.height = Math.min(300, rect.width * 0.5);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setDrawing(true);
    setHasStroke(true);
    const canvas = canvasRef.current;
    lastPos.current = getPos(e, canvas);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    lastPos.current = pos;
  };

  const endDraw = (e) => {
    e?.preventDefault();
    setDrawing(false);
    lastPos.current = null;
  };

  const limpiar = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasStroke(false);
  };

  const handleGuardar = async () => {
    if (!hasStroke) { err("Por favor dibuje su firma antes de guardar"); return; }
    setSaving(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await guardarFirma(id, dataUrl);
      ok("Firma guardada correctamente");
      setFirmada(true);
    } catch (e) {
      err(e?.response?.data || "Error al guardar la firma");
    } finally {
      setSaving(false);
    }
  };

  const money = (n) => n != null ? Number(n).toLocaleString("es-MX", { style: "currency", currency: "MXN" }) : "—";
  const fmt   = (d) => d ? new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" }) : "—";

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: "16px" }}>
      <Toast toasts={toasts} onClose={closeToast} />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button className="lmd-btn lmd-btn-outline lmd-btn-xs"
          onClick={() => navigate(`/servicios/ordenes/${id}/editar`)}>
          ←
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Firma del cliente</h2>
          {orden && <p style={{ margin: 0, fontSize: 13, color: "var(--clr-text-muted)" }}>Orden #{orden.ordenTrabajoId} — {orden.nombreCliente}</p>}
        </div>
      </div>

      {/* Resumen de la orden */}
      {orden && (
        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 20, fontSize: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
            <div><span style={{ color: "var(--clr-text-muted)", fontSize: 12 }}>Fecha entrada:</span><br /><strong>{fmt(orden.fechaEntrada)}</strong></div>
            <div><span style={{ color: "var(--clr-text-muted)", fontSize: 12 }}>Fecha entrega:</span><br /><strong>{fmt(orden.fechaEntrega)}</strong></div>
            <div style={{ gridColumn: "span 2" }}><span style={{ color: "var(--clr-text-muted)", fontSize: 12 }}>Trabajo:</span><br /><strong>{orden.trabajo}</strong></div>
            {orden.total != null && (
              <div><span style={{ color: "var(--clr-text-muted)", fontSize: 12 }}>Total:</span><br /><strong style={{ color: "#1d4ed8" }}>{money(orden.total)}</strong></div>
            )}
          </div>
        </div>
      )}

      {firmada ? (
        <div style={{ textAlign: "center", padding: "32px 0" }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
          <p style={{ fontWeight: 700, fontSize: 18, color: "#16a34a", margin: "0 0 6px" }}>Orden firmada</p>
          <p style={{ color: "var(--clr-text-muted)", fontSize: 14 }}>El cliente ya firmó esta orden de servicio.</p>
          <button className="lmd-btn lmd-btn-outline" style={{ marginTop: 20 }}
            onClick={() => navigate("/servicios/ordenes")}>
            Ver todas las órdenes
          </button>
        </div>
      ) : (
        <>
          <p style={{ fontSize: 14, color: "var(--clr-text-muted)", marginBottom: 12 }}>
            Por favor firme en el recuadro de abajo con su dedo o stylus.
          </p>

          {/* Canvas */}
          <div style={{
            border: "2px solid #cbd5e1", borderRadius: 8, background: "#fff",
            overflow: "hidden", touchAction: "none", cursor: "crosshair",
            userSelect: "none", WebkitUserSelect: "none",
          }}>
            <canvas
              ref={canvasRef}
              style={{ display: "block", width: "100%" }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
            />
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button className="lmd-btn lmd-btn-outline" onClick={limpiar}>
              Limpiar
            </button>
            <button className="lmd-btn lmd-btn-primary" onClick={handleGuardar} disabled={saving || !hasStroke}
              style={{ flex: 1 }}>
              {saving ? "Guardando…" : "Confirmar firma"}
            </button>
          </div>

          <p style={{ fontSize: 12, color: "var(--clr-text-muted)", marginTop: 12, textAlign: "center" }}>
            Al firmar, el cliente acepta el trabajo descrito y el precio estimado.
          </p>
        </>
      )}
    </div>
  );
}
